import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  MoreVert,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { DataService } from '../services/dataService';
import { KPIData, ChartData } from '../types';

const DashboardScreen: React.FC = () => {
  const [dateFilter, setDateFilter] = useState<'day' | 'week' | 'month'>('month');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const handleDateFilterChange = (event: SelectChangeEvent) => {
    setDateFilter(event.target.value as 'day' | 'week' | 'month');
  };

  const { data: kpiData, isLoading, error } = useQuery<KPIData>({
    queryKey: ['kpiData'],
    queryFn: DataService.getKPIData,
  });

  const { data: chartData, isLoading: isChartLoading, error: chartError } = useQuery<ChartData>({
    queryKey: ['chartData'],
    queryFn: DataService.getChartData,
  });

  if (isLoading || isChartLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || chartError) {
    return <Alert severity="error">Failed to fetch data.</Alert>;
  }

  if (!kpiData || !chartData) {
    return <Alert severity="warning">No data available.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" gutterBottom>
            Tableau de bord
          </Typography>
          
          {/* Period Indicator Chip */}
          <Chip
            label={
              dateFilter === 'day' ? 'Aujourd\'hui' :
              dateFilter === 'week' ? 'Cette semaine' :
              'Ce mois'
            }
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
        
        {/* Date Filter Selector */}
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="date-filter-label">Période</InputLabel>
          <Select
            labelId="date-filter-label"
            id="date-filter"
            value={dateFilter}
            label="Période"
            onChange={handleDateFilterChange}
          >
            <MenuItem value="day">Aujourd'hui</MenuItem>
            <MenuItem value="week">Cette semaine</MenuItem>
            <MenuItem value="month">Ce mois</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {/* KPI Tiles */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="overline">
                  Leads générés
                </Typography>
                <Typography variant="h4" component="div">
                  {kpiData.leadsGenerated[dateFilter]}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {dateFilter === 'day' && 'Aujourd\'hui'}
                  {dateFilter === 'week' && 'Cette semaine'}
                  {dateFilter === 'month' && 'Ce mois'}
                </Typography>
              </Box>
              <IconButton size="small">
                <MoreVert />
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="overline">
                  Mandats signés
                </Typography>
                <Typography variant="h4" component="div">
                  {kpiData.signedMandates[dateFilter]}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {dateFilter === 'day' && 'Aujourd\'hui'}
                  {dateFilter === 'week' && 'Cette semaine'}
                  {dateFilter === 'month' && 'Ce mois'}
                </Typography>
              </Box>
              <IconButton size="small">
                <MoreVert />
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="overline">
                  Taux de conversion
                </Typography>
                <Typography variant="h4" component="div">
                  {kpiData.conversionRate}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Taux global
                </Typography>
              </Box>
              <IconButton size="small">
                <MoreVert />
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="overline">
                  Commissions à payer
                </Typography>
                <Typography variant="h4" component="div">
                  {formatCurrency(kpiData.commissionsToPay)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatCurrency(kpiData.commissionsValidated)} validées
                </Typography>
              </Box>
              <IconButton size="small">
                <MoreVert />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Summary Comparison Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Résumé de la période
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Leads générés
              </Typography>
              <Typography variant="h6">
                {kpiData.leadsGenerated[dateFilter]}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {dateFilter === 'day' && 'Aujourd\'hui'}
                {dateFilter === 'week' && 'Cette semaine'}
                {dateFilter === 'month' && 'Ce mois'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Mandats signés
              </Typography>
              <Typography variant="h6">
                {kpiData.signedMandates[dateFilter]}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {dateFilter === 'day' && 'Aujourd\'hui'}
                {dateFilter === 'week' && 'Cette semaine'}
                {dateFilter === 'month' && 'Ce mois'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Taux de conversion
              </Typography>
              <Typography variant="h6">
                {kpiData.conversionRate}%
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Taux global
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Charts Section */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
        {/* Leads per Month Chart */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Évolution des leads
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData.leadsPerMonth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#2196F3" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        {/* Conversion Funnel */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Funnel de conversion
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={chartData.conversionFunnel}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ stage, count }) => {
                    // Show labels for all slices, but keep them simple
                    return `${stage}: ${count}`;
                  }}
                  outerRadius={90}
                  innerRadius={30}
                  fill="#8884d8"
                  dataKey="count"
                  paddingAngle={3}
                  minAngle={3}
                >
                  {chartData.conversionFunnel.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [value, name]}
                  labelFormatter={(label) => label}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          
          {/* Legend */}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {chartData.conversionFunnel.map((entry, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: entry.color,
                  }}
                />
                <Typography variant="caption" color="textSecondary">
                  {entry.stage}: {entry.count}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      {/* Top Performers Section */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Top Sponsors */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Top 5 des parrains
          </Typography>
          <Box>
            {chartData.topSponsors.map((sponsor, index) => (
              <Box
                key={sponsor.name}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: index < chartData.topSponsors.length - 1 ? '1px solid #eee' : 'none',
                }}
              >
                <Box>
                  <Typography variant="subtitle2">{sponsor.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {sponsor.leads} leads
                  </Typography>
                </Box>
                <Chip
                  label={`${sponsor.conversion}%`}
                  color={sponsor.conversion >= 80 ? 'success' : sponsor.conversion >= 70 ? 'warning' : 'error'}
                  size="small"
                />
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Top Agencies */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Top 5 des agences
          </Typography>
          <Box>
            {chartData.topAgencies.map((agency, index) => (
              <Box
                key={agency.name}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: index < chartData.topAgencies.length - 1 ? '1px solid #eee' : 'none',
                }}
              >
                <Box>
                  <Typography variant="subtitle2">{agency.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {agency.leads} leads reçus
                  </Typography>
                </Box>
                <Chip
                  label={`${agency.mandates} mandats`}
                  color="primary"
                  size="small"
                />
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default DashboardScreen;
